import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { theme } from "../../theme";
import Typography from "../Typography";
import CloseIcon from "../Icons/CloseIcon";
import NotificationOutlineIcon from "../Icons/NotificationOutlineIcon";
import PaidTicketIconOutline from "../Icons/PaidTicketIconOutline";
import TicketOutlineIcon from "../Icons/TicketOutlineIcon";
import ProfileIconOutline from "../Icons/ProfileIconOutline";

export const SETTINGS_TABS = [
	{
		key: "notifications",
		labelKey: "settings.notifications.title",
		Icon: NotificationOutlineIcon,
	},
	{
		key: "billing",
		labelKey: "settings.billing.title",
		Icon: PaidTicketIconOutline,
	},
	{
		key: "payments",
		labelKey: "settings.payments.title",
		Icon: TicketOutlineIcon,
	},
	{
		key: "account",
		labelKey: "settings.account.title",
		Icon: ProfileIconOutline,
	},
];

function UserSettingsSidebar({
	title,
	closeLabel,
	activeTab,
	onTabChange,
	onClose,
}) {
	const { t } = useTranslation("userProfile");

	return (
		<SettingsSidebar aria-label={title}>
			{onClose && (
				<SettingsSidebarHeader>
					<SidebarCloseButton
						type="button"
						onClick={onClose}
						aria-label={closeLabel}
					>
						<CloseIcon width={20} height={20} fill={theme.colors.defaultMain} />
					</SidebarCloseButton>
					<SidebarMobileHeader>
						<Typography as="span" $variant="body-1-semibold">
							{title}
						</Typography>
						<SidebarMobileCloseButton
							type="button"
							onClick={onClose}
							aria-label={closeLabel}
						>
							<CloseIcon
								width={20}
								height={20}
								fill={theme.colors.defaultMain}
							/>
						</SidebarMobileCloseButton>
					</SidebarMobileHeader>
				</SettingsSidebarHeader>
			)}
			<SettingsTabList role="tablist" aria-label={title}>
				{SETTINGS_TABS.map(({ key, labelKey, Icon }) => {
					const isActive = activeTab === key;
					return (
						<SettingsTabButton
							key={key}
							id={`settings-tab-${key}`}
							type="button"
							role="tab"
							aria-selected={isActive}
							aria-controls={`settings-panel-${key}`}
							tabIndex={isActive ? 0 : -1}
							$active={isActive}
							onClick={() => onTabChange(key)}
						>
							<IconSlot>
								<Icon
									width={18}
									height={18}
									fill={
										isActive
											? theme.colors.defaultStrong
											: theme.colors.defaultWeak
									}
								/>
							</IconSlot>
							<Typography as="span" $variant="body-1-medium" color="inherit">
								{t(labelKey)}
							</Typography>
						</SettingsTabButton>
					);
				})}
			</SettingsTabList>
		</SettingsSidebar>
	);
}

UserSettingsSidebar.propTypes = {
	title: PropTypes.string.isRequired,
	closeLabel: PropTypes.string.isRequired,
	activeTab: PropTypes.oneOf(SETTINGS_TABS.map(({ key }) => key)).isRequired,
	onTabChange: PropTypes.func.isRequired,
	onClose: PropTypes.func,
};

export default UserSettingsSidebar;

const SettingsSidebar = styled.aside`
	width: 240px;
	max-width: 240px;
	flex-shrink: 0;
	padding: ${({ theme }) => theme.sizing.xs};
	background: ${({ theme }) => theme.fill.defaultMain};
	align-self: flex-start;
	position: sticky;
	top: 0;
	z-index: 2;

	@media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
		width: 100%;
		position: sticky;
		top: 0;
		max-width: 100%;
		padding: 0;
	}
`;

const SettingsSidebarHeader = styled.div`
	@media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
		background: ${({ theme }) => theme.fill.defaultMain};
	}
`;

const SidebarCloseButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 4px;
	margin-bottom: 12px;
	border: none;
	background: transparent;
	border-radius: ${({ theme }) => theme.radius.xs};
	cursor: pointer;

	&:hover {
		background: ${({ theme }) => theme.fill.defaultWeak};
	}

	@media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
		display: none;
	}
`;

const SidebarMobileHeader = styled.div`
	display: none;

	@media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: ${({ theme }) => theme.sizing.sm};
		padding-bottom: ${({ theme }) => theme.sizing.xs};
	}
`;

const SidebarMobileCloseButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 4px;
	border: none;
	background: transparent;
	border-radius: ${({ theme }) => theme.radius.xs};
	cursor: pointer;

	&:hover {
		background: ${({ theme }) => theme.fill.defaultWeak};
	}
`;

const SettingsTabList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;


	@media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
		flex-direction: row;
		overflow-x: auto;
		padding-left: ${({ theme }) => theme.sizing.sm};
		padding-top: ${({ theme }) => theme.sizing.xxs};
		padding-bottom: ${({ theme }) => theme.sizing.sm};
		border-bottom: 1px solid ${({ theme }) => theme.border.defaultWeak};
	}
`;

const SettingsTabButton = styled.button`
	display: flex;
	align-items: center;
	padding: 12px;
	gap: ${({ theme }) => theme.sizing.xs};
	width: 100%;
	border: none;
	border-radius: ${({ theme }) => theme.radius.xs};
	background: ${({ theme, $active }) =>
		$active ? theme.fill.defaultWeak : "transparent"};
	color: ${({ theme, $active }) =>
		$active ? theme.colors.defaultStrong : theme.colors.defaultWeak};
	cursor: pointer;
	text-align: left;

	@media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
		padding: 6px 8px 6px 6px;
		width: auto;
		white-space: nowrap;
	}
`;

const IconSlot = styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
`;
